import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useRsvp } from '@/src/modules/rsvp';

interface RSVPFarewellProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
}

export const RSVPFarewellSection: React.FC<RSVPFarewellProps> = ({ invitationId, isLive, language }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const isSubmitting = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || attending === null || isSubmitting) return;

    await submit({
      guestName: name.trim(),
      guestEmail: email.trim() || undefined,
      status: attending ? 'ACCEPTED' : 'DECLINED',
      headcount: 1,
      message: message.trim() || undefined,
    });
  };

  if (state === 'success') {
    return (
      <section className="relative py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle2 size={40} className="text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white mb-4">
            {language === 'mr' ? 'धन्यवाद!' : 'Thank You!'}
          </h2>
          <p className="text-gray-300 text-lg">
            {language === 'mr'
              ? 'आपली RSVP स्वीकारली गेली आहे। विदाईला तुमची उपस्थिती महत्वाची आहे।'
              : 'Your RSVP has been received. See you at the farewell!'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-transparent via-slate-900/20 to-slate-900/50">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400 mb-12">
          {language === 'mr' ? '✋ RSVP करा' : '✋ Confirm Your Attendance'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'नाव' : 'Name'} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={language === 'mr' ? 'पूर्ण नाव' : 'Full name'}
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'ईमेल' : 'Email'}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-4">
              {language === 'mr' ? 'आप येणार आहात?' : 'Will you attend?'} *
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setAttending(true)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === true
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg shadow-blue-500/50'
                    : 'bg-slate-800 border border-slate-700 text-gray-300 hover:border-blue-400'
                }`}
              >
                {language === 'mr' ? '✓ होय' : '✓ Yes'}
              </button>
              <button
                type="button"
                onClick={() => setAttending(false)}
                className={`py-4 px-6 rounded-lg font-bold transition-all ${
                  attending === false
                    ? 'bg-gradient-to-r from-pink-500 to-red-600 text-white shadow-lg shadow-pink-500/50'
                    : 'bg-slate-800 border border-slate-700 text-gray-300 hover:border-pink-400'
                }`}
              >
                {language === 'mr' ? '✕ नाही' : '✕ No'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'संदेश' : 'Message'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={language === 'mr' ? 'आपला संदेश...' : 'Your message...'}
              rows={3}
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 transition-all resize-none"
            />
          </div>

          {state === 'error' && errorMessage && (
            <p className="text-red-400 text-sm text-center font-semibold">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={attending === null || isSubmitting}
            className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white font-bold py-4 rounded-lg hover:shadow-2xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? (language === 'mr' ? 'सबमिट करत आहे...' : 'Submitting...')
              : (language === 'mr' ? 'RSVP सबमिट करा' : 'Submit RSVP')}
          </button>
        </form>
      </div>
    </section>
  );
};
